import React, { Fragment, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faFileInvoice } from "@fortawesome/free-solid-svg-icons";
import util from "../../utils/miniUtils";
import { useHistory, useLocation } from "react-router-dom";
import {
    PAYMENT_STATUS_FILTERS, PAYMENT_STATUS_NONE, SHIPPING_LABELS, formatDateTime, formatMoney, formatPhoneMx, paymentBadgeClass, paymentDetailLabel, paymentStatusLabel,
    shippingBadgeClass, showApiError,
} from "./salesHelpers";
import "./Sales.css";

// ORDERS_MAX_LIMIT de inventory-api es 100
const PAGE_SIZES = [ 10, 25, 50, 100 ];
const DEFAULT_PAGE_SIZE = 10;
const EMPTY_FILTERS = { from: "", to: "", paymentStatus: "", shippingStatus: "", onlyAlerts: false, onlyInvoicePending: false, q: "" };

// Tooltips de la fila: lo que ya no cabe en la tabla compacta
const customerTooltip = ( customer ) => [ customer.email, customer.phone && formatPhoneMx( customer.phone ) ].filter( Boolean ).join( " · " );
const paymentTooltip = ( order ) => {
    if ( !order.paymentStatus ) return "Sin intentos de pago";
    const detail = order.paymentStatusDetail ? ` · ${paymentDetailLabel( order.paymentStatus, order.paymentStatusDetail )}` : "";
    return `${order.paymentStatus}${detail}`;
};

// La lista vive en la URL (?page=20&limit=25&q=...) para que al volver del detalle quede donde estaba
const readQuery = ( search ) => {
    const params = new URLSearchParams( search );
    const page = Number( params.get( "page" ) );
    const pageSize = Number( params.get( "limit" ) );
    const filters = { ...EMPTY_FILTERS };
    Object.keys( EMPTY_FILTERS ).forEach( ( key ) => {
        const value = params.get( key );
        const isCheckbox = typeof EMPTY_FILTERS[ key ] === "boolean";
        if ( value !== null ) filters[ key ] = isCheckbox ? value === "true" : value;
    } );
    return {
        filters,
        page: Number.isInteger( page ) && page > 0 ? page : 1,
        pageSize: PAGE_SIZES.includes( pageSize ) ? pageSize : DEFAULT_PAGE_SIZE,
    };
};

// Solo se escribe lo que difiere del valor inicial
const buildQuery = ( filters, page, pageSize ) => {
    const params = new URLSearchParams();
    Object.keys( EMPTY_FILTERS ).forEach( ( key ) => {
        if ( filters[ key ] !== EMPTY_FILTERS[ key ] ) params.set( key, filters[ key ] );
    } );
    if ( page > 1 ) params.set( "page", page );
    if ( pageSize !== DEFAULT_PAGE_SIZE ) params.set( "limit", pageSize );
    return params.toString();
};

// Panel de Ventas (§7.1): pedidos de la tienda con su Estado de MercadoPago y su Envío por separado.
// El orden (alertas primero, luego "Por enviar" del más antiguo) lo decide GET /sales/orders.
export default function Sales() {
    const [ orders, setOrders ] = useState( [] );
    const [ pagination, setPagination ] = useState( { page: 1, totalPages: 0, total: 0 } );
    const [ summary, setSummary ] = useState( { paidOrders: 0, paidTotal: 0, alertOrders: 0 } );
    const history = useHistory();
    const location = useLocation();
    const [ initialQuery ] = useState( () => readQuery( location.search ) );
    const [ filters, setFilters ] = useState( initialQuery.filters );
    const [ appliedFilters, setAppliedFilters ] = useState( initialQuery.filters );
    const [ page, setPage ] = useState( initialQuery.page );
    const [ pageSize, setPageSize ] = useState( initialQuery.pageSize );
    const [ loading, setLoading ] = useState( false );

    useEffect( () => {
        history.replace( { search: buildQuery( appliedFilters, page, pageSize ) } );
        loadOrders().then( null );
    }, [ appliedFilters, page, pageSize ] );

    async function loadOrders() {
        try {
            setLoading( true );
            const params = { ...appliedFilters, page, limit: pageSize };
            const request = await util.axios.get( "sales/orders", { params } );
            const { error, msg, data } = request.data;
            if ( error ) throw msg;
            setOrders( data.rows );
            setPagination( data.pagination );
            setSummary( data.summary );
        } catch ( e ) {
            showApiError( e );
        } finally {
            setLoading( false );
        }
    }

    const handleFilterChange = ( event ) => {
        const { name, value, type, checked } = event.target;
        setFilters( { ...filters, [name]: type === "checkbox" ? checked : value } );
    };

    const handleSearch = ( event ) => {
        event.preventDefault();
        setPage( 1 );
        setAppliedFilters( filters );
    };

    const handleClearFilters = () => {
        setFilters( EMPTY_FILTERS );
        setPage( 1 );
        setAppliedFilters( EMPTY_FILTERS );
    };

    // Atajo del aviso: aplica el filtro "Solo con alerta" sobre los demás filtros vigentes
    const handleShowAlerts = () => {
        const alertFilters = { ...appliedFilters, onlyAlerts: true };
        setFilters( alertFilters );
        setPage( 1 );
        setAppliedFilters( alertFilters );
    };

    const handlePageSizeChange = ( event ) => {
        setPage( 1 );
        setPageSize( Number( event.target.value ) );
    };

    const isFirstPage = page <= 1;
    const isLastPage = page >= pagination.totalPages;

    return (
        <div className="container-fluid mb-3 mt-3">
            <form className="sales-filters row g-2 align-items-end" onSubmit={handleSearch} data-cy="sales-filters">
                <div className="col-md-3">
                    <label htmlFor="sales-q" className="form-label small mb-0">Buscar</label>
                    <input id="sales-q" name="q" className="form-control form-control-sm" placeholder="Pedido, nombre, correo o teléfono"
                        value={filters.q} onChange={handleFilterChange} data-cy="sales-search" />
                </div>
                <div className="col-md-2">
                    <label htmlFor="sales-from" className="form-label small mb-0">Desde</label>
                    <input id="sales-from" name="from" type="date" className="form-control form-control-sm"
                        value={filters.from} onChange={handleFilterChange} data-cy="sales-from" />
                </div>
                <div className="col-md-2">
                    <label htmlFor="sales-to" className="form-label small mb-0">Hasta</label>
                    <input id="sales-to" name="to" type="date" className="form-control form-control-sm"
                        value={filters.to} onChange={handleFilterChange} data-cy="sales-to" />
                </div>
                <div className="col-md-2">
                    <label htmlFor="sales-payment-status" className="form-label small mb-0">Estado MercadoPago</label>
                    <select id="sales-payment-status" name="paymentStatus" className="form-select form-select-sm"
                        value={filters.paymentStatus} onChange={handleFilterChange} data-cy="sales-payment-status">
                        <option value="">Todos</option>
                        <option value={PAYMENT_STATUS_NONE}>Sin pago</option>
                        {PAYMENT_STATUS_FILTERS.map( ( status ) => <option key={status} value={status}>{paymentStatusLabel( status )}</option> )}
                    </select>
                </div>
                <div className="col-md-2">
                    <label htmlFor="sales-shipping-status" className="form-label small mb-0">Envío</label>
                    <select id="sales-shipping-status" name="shippingStatus" className="form-select form-select-sm"
                        value={filters.shippingStatus} onChange={handleFilterChange} data-cy="sales-shipping-status">
                        <option value="">Todos</option>
                        {Object.keys( SHIPPING_LABELS ).map( ( status ) => <option key={status} value={status}>{SHIPPING_LABELS[status]}</option> )}
                    </select>
                </div>
                <div className="col-md-2 ms-2">
                    <div className="form-check">
                        <input id="sales-only-alerts" name="onlyAlerts" type="checkbox" className="form-check-input"
                            checked={filters.onlyAlerts} onChange={handleFilterChange} data-cy="sales-only-alerts" />
                        <label htmlFor="sales-only-alerts" className="form-check-label small">Solo con alerta</label>
                    </div>
                    <div className="form-check">
                        <input id="sales-only-invoice-pending" name="onlyInvoicePending" type="checkbox" className="form-check-input"
                            checked={filters.onlyInvoicePending} onChange={handleFilterChange} data-cy="sales-only-invoice-pending" />
                        <label htmlFor="sales-only-invoice-pending" className="form-check-label small">Factura pendiente</label>
                    </div>
                </div>
                <div className="col-12 d-flex gap-2">
                    <button type="submit" className="btn btn-sm btn-primary" data-cy="sales-apply-filters">Buscar</button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleClearFilters} data-cy="sales-clear-filters">Limpiar</button>
                </div>
            </form>

            <div className="sales-summary my-3" data-cy="sales-summary">
                <span>Pedidos pagados: <strong>{summary.paidOrders}</strong></span>
                <span>Monto pagado: <strong>{formatMoney( summary.paidTotal )}</strong></span>
                <span className="text-muted">({pagination.total} pedidos en el filtro)</span>
            </div>

            {summary.alertOrders > 0 && (
                <div className="alert alert-danger d-flex align-items-center py-2" data-cy="sales-alerts-warning">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    <span>
                        Hay <strong>{summary.alertOrders}</strong> {summary.alertOrders === 1 ? "pedido" : "pedidos"} que requieren atención.
                    </span>
                    <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={handleShowAlerts} data-cy="sales-show-alerts">
                        Ver pedidos con alerta
                    </button>
                </div>
            )}

            <div className="table-responsive">
                <table className="table table-sm table-hover align-middle sales-table" data-cy="sales-table">
                    <thead>
                        <tr>
                            <th className="text-end">#</th>
                            <th>Pedido</th>
                            <th>Fecha</th>
                            <th>Cliente</th>
                            <th className="text-end">Pzs</th>
                            <th className="text-end">Total</th>
                            <th>Pago</th>
                            <th>Envío</th>
                            <th>Guía</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.length === 0 && !loading && (
                            <tr><td colSpan={9} className="text-center text-muted">No hay pedidos con estos filtros</td></tr>
                        )}
                        {orders.map( ( order, position ) => (
                            <tr key={order.uuid} className={order.hasAlert ? "sales-row-alert" : ""}>
                                <td className="text-end text-muted">{( page - 1 ) * pageSize + position + 1}</td>
                                <td className="text-nowrap">
                                    {order.hasAlert && (
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-1" title="Requiere atención" />
                                    )}
                                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => history.push( `/ventas/${order.uuid}`, { listSearch: location.search } )}
                                        data-cy={`sales-open-${order.orderNumber}`}>
                                        #{order.orderNumber}
                                    </button>
                                    {order.invoiceRequested && !order.invoiced && !order.cancelled && (
                                        <FontAwesomeIcon icon={faFileInvoice} className="sales-invoice-pending ms-1" title="Factura solicitada: pendiente de subir" data-cy={`sales-invoice-pending-${order.orderNumber}`} />
                                    )}
                                    {order.invoiced && (
                                        <FontAwesomeIcon icon={faFileInvoice} className="text-success ms-1" title="Facturado" />
                                    )}
                                    {order.cancelled && <span className="badge bg-dark ms-1">Cancelado</span>}
                                </td>
                                <td className="text-nowrap">{formatDateTime( order.createdAt )}</td>
                                <td className="sales-customer" title={customerTooltip( order.customer )}>
                                    {order.customer.firstName} {order.customer.lastName}
                                    <span className="text-muted ms-2">{order.customer.email}</span>
                                </td>
                                <td className="text-end">{order.itemCount}</td>
                                <td className="text-end sales-money">{formatMoney( order.total )}</td>
                                <td className="text-nowrap" title={paymentTooltip( order )}>
                                    <span className={paymentBadgeClass( order.paymentStatus )}>
                                        {order.paymentStatus ? paymentStatusLabel( order.paymentStatus ) : "Sin pago"}
                                    </span>
                                </td>
                                <td className="text-nowrap">
                                    {order.shippingStatus
                                        ? <span className={shippingBadgeClass( order.shippingStatus )}>{SHIPPING_LABELS[order.shippingStatus]}</span>
                                        : <span className="text-muted">—</span>}
                                </td>
                                <td className="text-nowrap">
                                    {order.trackingNumber
                                        ? <Fragment>{order.trackingNumber}<span className="text-muted ms-1">{order.carrier}</span></Fragment>
                                        : <span className="text-muted">—</span>}
                                </td>
                            </tr>
                        ) )}
                    </tbody>
                </table>
            </div>

            <div className="d-flex align-items-center gap-2" data-cy="sales-pagination">
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={isFirstPage} onClick={() => setPage( page - 1 )}
                    data-cy="sales-prev-page">Anterior</button>
                <span className="small">Página {pagination.totalPages === 0 ? 0 : page} de {pagination.totalPages}</span>
                <button type="button" className="btn btn-sm btn-outline-secondary" disabled={isLastPage} onClick={() => setPage( page + 1 )}
                    data-cy="sales-next-page">Siguiente</button>
                <label htmlFor="sales-page-size" className="small ms-3">Mostrar</label>
                <select id="sales-page-size" className="form-select form-select-sm sales-page-size" value={pageSize}
                    onChange={handlePageSizeChange} data-cy="sales-page-size">
                    {PAGE_SIZES.map( ( size ) => <option key={size} value={size}>{size}</option> )}
                </select>
                <span className="small">por página</span>
            </div>

            {util.LOADING_SCREEN( loading )}
        </div>
    );
}
